import type { AxiosError } from 'axios';
import { authClient } from './client';
import type { SalePaymentStatus } from '../types/consignorSales';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ConsignorDashboardMetrics {
  /** Variants currently active (listed for sale) */
  activeItems: number;
  /** Total completed sales ever */
  soldItems: number;
  /** Sum of payoutAmount for completed sales with paymentId: null (status=pending) */
  pendingPayoutAmount: number;
  /** Variants in photo_queue or cleaning_queue — not yet listed for sale */
  reviewItems: number;
  /** True if there is an open unpaid payment batch */
  hasPendingPayment: boolean;
  /** Amount in the open payment batch (0 if none) */
  pendingPaymentAmount: number;
  /**
   * Lifetime sum of gross sale amounts (checkout / list price at time of sale)
   * for completed sales attributable to this consignor — MXN.
   */
  lifetimeSaleAmount: number;
}

export interface RecentSaleItem {
  id: number;
  productName: string;
  variantName: string;
  imageUrl: string | null;
  soldAt: string;
  payoutAmount: number;
  paymentStatus: SalePaymentStatus;
}

export interface ConsignorDashboardData {
  metrics: ConsignorDashboardMetrics;
  /** Last 3 completed sales ordered by soldAt DESC */
  recentSales: RecentSaleItem[];
}

// ─── Parsers ──────────────────────────────────────────────────────────────────

const VALID_STATUSES: SalePaymentStatus[] = ['pending', 'scheduling', 'scheduled', 'paid'];

function parseMetrics(raw: unknown): ConsignorDashboardMetrics {
  const empty: ConsignorDashboardMetrics = {
    activeItems: 0,
    soldItems: 0,
    pendingPayoutAmount: 0,
    reviewItems: 0,
    hasPendingPayment: false,
    pendingPaymentAmount: 0,
    lifetimeSaleAmount: 0,
  };
  if (!raw || typeof raw !== 'object') return empty;
  const r = raw as Record<string, unknown>;
  return {
    activeItems: Number(r.activeItems ?? r.active_items ?? 0),
    soldItems: Number(r.soldItems ?? r.sold_items ?? 0),
    pendingPayoutAmount: Number(r.pendingPayoutAmount ?? r.pending_payout_amount ?? 0),
    reviewItems: Number(r.reviewItems ?? r.review_items ?? 0),
    hasPendingPayment: Boolean(r.hasPendingPayment ?? r.has_pending_payment ?? false),
    pendingPaymentAmount: Number(r.pendingPaymentAmount ?? r.pending_payment_amount ?? 0),
    lifetimeSaleAmount: Number(r.lifetimeSaleAmount ?? r.lifetime_sale_amount ?? 0),
  };
}

function parseRecentSale(raw: unknown): RecentSaleItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const imageUrlRaw = r.imageUrl ?? r.image_url ?? r.thumbnailUrl;
  const soldAtRaw = r.soldAt ?? r.sold_at;
  const statusRaw = String(r.paymentStatus ?? r.payment_status ?? 'pending');
  return {
    id,
    productName: String(r.productName ?? r.product_name ?? '').trim(),
    variantName: String(r.variantName ?? r.variant_name ?? '').trim(),
    imageUrl: typeof imageUrlRaw === 'string' && imageUrlRaw.trim() ? imageUrlRaw.trim() : null,
    soldAt: typeof soldAtRaw === 'string' && soldAtRaw ? soldAtRaw : new Date(0).toISOString(),
    payoutAmount: Number(r.payoutAmount ?? r.payout_amount ?? 0),
    paymentStatus: VALID_STATUSES.includes(statusRaw as SalePaymentStatus)
      ? (statusRaw as SalePaymentStatus)
      : 'pending',
  };
}

// ─── Exported function ────────────────────────────────────────────────────────

export type ConsignorDashboardResult =
  | { ok: true; data: ConsignorDashboardData }
  | { ok: false; error: string };

/**
 * GET /api/consignor/dashboard
 *
 * Returns the consignor's key metrics and their 3 most recent sales.
 * Auth: authenticated consignor (non-admin).
 *
 * Response 200:
 * {
 *   "data": {
 *     "metrics": {
 *       "activeItems": 12,
 *       "soldItems": 45,
 *       "pendingPayoutAmount": 3500.00,   // sum for sales with paymentId: null
 *       "reviewItems": 3,                 // variants in photo_queue or cleaning_queue
 *       "hasPendingPayment": false,       // true if open unpaid payment batch exists
 *       "pendingPaymentAmount": 0,        // amount in that batch (0 if none)
 *       "lifetimeSaleAmount": 125000.00   // sum of gross completed sale totals (lifetime)
 *     },
 *     "recentSales": [
 *       {
 *         "id": 1,
 *         "productName": "Jordan 1 High",
 *         "variantName": "10.5 US · Deadstock",
 *         "imageUrl": "https://...",
 *         "soldAt": "2024-10-15T00:00:00Z",
 *         "payoutAmount": 1750.00,
 *         "paymentStatus": "pending"  // pending | scheduling | scheduled | paid
 *       }
 *     ]
 *   }
 * }
 *
 * Notes:
 *   - recentSales: last 3 COMPLETED transactions, ordered soldAt DESC.
 *   - payoutAmount must never expose publicPrice or commission rate.
 * Errors: 403, 500.
 */
export async function fetchConsignorDashboard(): Promise<ConsignorDashboardResult> {
  try {
    const { data } = await authClient.get<unknown>('/api/consignor/dashboard');
    const body = data as Record<string, unknown>;
    const inner = (typeof body.data === 'object' && body.data ? body.data : body) as Record<
      string,
      unknown
    >;

    const rawSales = inner.recentSales ?? inner.recent_sales;

    return {
      ok: true,
      data: {
        metrics: parseMetrics(inner.metrics),
        recentSales: Array.isArray(rawSales)
          ? rawSales.map(parseRecentSale).filter((x): x is RecentSaleItem => x !== null)
          : [],
      },
    };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo cargar el dashboard.',
    };
  }
}
